import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiJson } from "../../lib/api/_http";
import { ApiClientError } from "../../lib/api/_errors";

// /admin — founder operational dashboard. Read-only. Gated server-side by the
// fam Worker (env.FOUNDER_USER_ID); non-founder callers get a 404 from the
// upstream and we render a "not authorized" shell. No nav entry — accessible
// by typing the URL.
//
// Auto-refreshes every 60s; six panels surface what just happened across the
// platform plus the freshness of every cron heartbeat.

interface AdminFamRow {
	id: string;
	slug: string | null;
	name: string;
	tier: string;
	isPersonal: boolean;
	visibility: string;
	memberCount: number;
	ideaCount: number;
	createdAt: string;
}

interface AdminUserRow {
	id: string;
	username: string;
	displayName: string | null;
	hasLinkedEmail: boolean;
	isDemo: boolean;
	createdAt: string;
}

interface AdminIdeaRow {
	id: string;
	famId: string;
	famSlug: string | null;
	symbol: string;
	direction: string;
	status: string;
	proposerUserId: string;
	proposerUsername: string;
	proposerRole: string;
	proposedAt: string;
	resolvesAt: string;
	resolvedAt: string | null;
}

interface AdminWebhookRow {
	id: string;
	type: string;
	receivedAt: string;
	processedAt: string | null;
}

interface AdminAuditRow {
	id: string;
	eventType: string;
	famId: string | null;
	famSlug: string | null;
	userId: string | null;
	payload: unknown;
	occurredAt: string;
}

interface AdminCronHeartbeat {
	name: string;
	lastRunAt: string | null;
	ageSeconds: number | null;
	expectedIntervalSeconds: number;
	stale: boolean;
	raw: string | null;
}

interface AdminDashboard {
	fams: AdminFamRow[];
	users: AdminUserRow[];
	ideas: AdminIdeaRow[];
	webhooks: AdminWebhookRow[];
	audit: AdminAuditRow[];
	crons: AdminCronHeartbeat[];
}

export function AdminRoute() {
	const dashboard = useQuery<AdminDashboard, ApiClientError>({
		queryKey: ["admin", "dashboard"],
		queryFn: () => apiJson<AdminDashboard>("/api/admin/dashboard"),
		refetchInterval: 60_000,
		retry: false,
	});

	// Upstream returns 404 for non-founders — surface as a stub message
	// rather than spilling the error envelope.
	if (dashboard.isError) {
		const err = dashboard.error;
		const is404 =
			err instanceof ApiClientError &&
			(err.status === 404 || err.code === "not_found");
		return (
			<div className="mx-auto max-w-3xl px-4 py-16 text-center">
				<h1 className="text-xl font-semibold">
					{is404 ? "Not authorized" : "Could not load admin dashboard"}
				</h1>
				<p className="mt-2 text-sm text-[var(--color-muted)]">
					{is404
						? "This page is restricted."
						: err instanceof Error
							? err.message
							: "Unknown error."}
				</p>
				<Link
					to="/"
					className="mt-6 inline-block rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)]"
				>
					← Dashboard
				</Link>
			</div>
		);
	}

	return (
		<div className="mx-auto max-w-6xl space-y-8 px-4 py-8">
			<header className="flex items-baseline justify-between">
				<div>
					<Link
						to="/"
						className="text-xs text-[var(--color-muted)] hover:text-[var(--color-fg)]"
					>
						← Dashboard
					</Link>
					<h1 className="mt-1 text-2xl font-bold">Admin</h1>
					<p className="text-sm text-[var(--color-muted)]">
						Operational state — refreshes every 60s.
					</p>
				</div>
				{dashboard.isFetching && (
					<span className="text-xs text-[var(--color-muted)]">Refreshing…</span>
				)}
			</header>

			{dashboard.isLoading && !dashboard.data && (
				<p className="text-sm text-[var(--color-muted)]">Loading…</p>
			)}

			{dashboard.data && (
				<>
					<CronPanel rows={dashboard.data.crons} />
					<FamsPanel rows={dashboard.data.fams} />
					<UsersPanel rows={dashboard.data.users} />
					<IdeasPanel rows={dashboard.data.ideas} />
					<WebhooksPanel rows={dashboard.data.webhooks} />
					<AuditPanel rows={dashboard.data.audit} />
				</>
			)}
		</div>
	);
}

// --- Panels -----------------------------------------------------------------

function PanelShell({
	title,
	subtitle,
	children,
}: {
	title: string;
	subtitle: string;
	children: React.ReactNode;
}) {
	return (
		<section className="space-y-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
			<div>
				<h2 className="text-sm font-semibold">{title}</h2>
				<p className="text-xs text-[var(--color-muted)]">{subtitle}</p>
			</div>
			<div className="overflow-x-auto">{children}</div>
		</section>
	);
}

function CronPanel({ rows }: { rows: AdminCronHeartbeat[] }) {
	return (
		<PanelShell
			title="Cron heartbeats"
			subtitle="Red when age > 2× expected interval. Missing = never fired in the last 7d (KV TTL)."
		>
			<ul className="divide-y divide-[var(--color-border)]">
				{rows.map((r) => (
					<li
						key={r.name}
						className="flex items-center justify-between gap-3 py-2 text-sm"
					>
						<div className="min-w-0">
							<p className="font-mono text-xs">{r.name}</p>
							<p className="text-xs text-[var(--color-muted)]">
								expected every {formatDuration(r.expectedIntervalSeconds)}
							</p>
						</div>
						<div className="text-right">
							<p
								className={
									r.stale
										? "text-sm font-medium text-[var(--color-danger)]"
										: "text-sm"
								}
							>
								{r.ageSeconds == null
									? "never"
									: `${formatDuration(r.ageSeconds)} ago`}
							</p>
							<p className="text-xs text-[var(--color-muted)]">
								{r.lastRunAt ?? "—"}
							</p>
						</div>
					</li>
				))}
			</ul>
		</PanelShell>
	);
}

function FamsPanel({ rows }: { rows: AdminFamRow[] }) {
	return (
		<PanelShell title="Recent fams" subtitle={`Last ${rows.length} created.`}>
			<Table headers={["Slug", "Name", "Tier", "Members", "Ideas", "Age"]}>
				{rows.map((r) => (
					<tr key={r.id} className="border-t border-[var(--color-border)]">
						<td className="px-2 py-1.5 font-mono text-xs">
							{r.slug ?? "—"}
							{r.isPersonal && (
								<span className="ml-1 text-[var(--color-muted)]">
									·personal
								</span>
							)}
						</td>
						<td className="px-2 py-1.5">{r.name}</td>
						<td className="px-2 py-1.5 text-xs">{r.tier}</td>
						<td className="px-2 py-1.5 tabular-nums">{r.memberCount}</td>
						<td className="px-2 py-1.5 tabular-nums">{r.ideaCount}</td>
						<td className="px-2 py-1.5 text-xs text-[var(--color-muted)]">
							{formatAgo(r.createdAt)}
						</td>
					</tr>
				))}
			</Table>
		</PanelShell>
	);
}

function UsersPanel({ rows }: { rows: AdminUserRow[] }) {
	return (
		<PanelShell title="Recent users" subtitle={`Last ${rows.length} signups.`}>
			<Table headers={["Handle", "Display", "Email?", "Demo", "Age"]}>
				{rows.map((r) => (
					<tr key={r.id} className="border-t border-[var(--color-border)]">
						<td className="px-2 py-1.5 font-mono text-xs">@{r.username}</td>
						<td className="px-2 py-1.5">{r.displayName ?? "—"}</td>
						<td className="px-2 py-1.5 text-xs">
							{r.hasLinkedEmail ? "yes" : "—"}
						</td>
						<td className="px-2 py-1.5 text-xs">{r.isDemo ? "yes" : "—"}</td>
						<td className="px-2 py-1.5 text-xs text-[var(--color-muted)]">
							{formatAgo(r.createdAt)}
						</td>
					</tr>
				))}
			</Table>
		</PanelShell>
	);
}

function IdeasPanel({ rows }: { rows: AdminIdeaRow[] }) {
	return (
		<PanelShell title="Recent ideas" subtitle={`Last ${rows.length} proposed.`}>
			<Table
				headers={["Fam", "Ticker", "Dir", "Status", "Proposer", "Role", "Age"]}
			>
				{rows.map((r) => (
					<tr key={r.id} className="border-t border-[var(--color-border)]">
						<td className="px-2 py-1.5 font-mono text-xs">
							{r.famSlug ?? r.famId.slice(0, 8)}
						</td>
						<td className="px-2 py-1.5 font-mono">{r.symbol}</td>
						<td className="px-2 py-1.5 text-xs">{r.direction}</td>
						<td className="px-2 py-1.5 text-xs">{r.status}</td>
						<td className="px-2 py-1.5 font-mono text-xs">
							@{r.proposerUsername}
						</td>
						<td className="px-2 py-1.5 text-xs">{r.proposerRole}</td>
						<td className="px-2 py-1.5 text-xs text-[var(--color-muted)]">
							{formatAgo(r.proposedAt)}
						</td>
					</tr>
				))}
			</Table>
		</PanelShell>
	);
}

function WebhooksPanel({ rows }: { rows: AdminWebhookRow[] }) {
	return (
		<PanelShell
			title="Stripe webhooks"
			subtitle={`Last ${rows.length} deliveries (migration 028).`}
		>
			<Table headers={["Event id", "Type", "Received", "Processed"]}>
				{rows.map((r) => (
					<tr key={r.id} className="border-t border-[var(--color-border)]">
						<td className="px-2 py-1.5 font-mono text-xs">
							{r.id.slice(0, 18)}…
						</td>
						<td className="px-2 py-1.5 text-xs">{r.type}</td>
						<td className="px-2 py-1.5 text-xs text-[var(--color-muted)]">
							{formatAgo(r.receivedAt)}
						</td>
						<td className="px-2 py-1.5 text-xs">
							{r.processedAt ? "yes" : "—"}
						</td>
					</tr>
				))}
				{rows.length === 0 && (
					<tr>
						<td
							colSpan={4}
							className="px-2 py-2 text-center text-xs text-[var(--color-muted)]"
						>
							No webhook deliveries recorded.
						</td>
					</tr>
				)}
			</Table>
		</PanelShell>
	);
}

function AuditPanel({ rows }: { rows: AdminAuditRow[] }) {
	return (
		<PanelShell
			title="System audit"
			subtitle={`Last ${rows.length} audit_log rows with actor=null (cron + system events).`}
		>
			<Table headers={["Event", "Fam", "Age", "Payload"]}>
				{rows.map((r) => (
					<tr
						key={r.id}
						className="border-t border-[var(--color-border)] align-top"
					>
						<td className="px-2 py-1.5 font-mono text-xs">{r.eventType}</td>
						<td className="px-2 py-1.5 font-mono text-xs">
							{r.famSlug ?? r.famId?.slice(0, 8) ?? "—"}
						</td>
						<td className="px-2 py-1.5 text-xs text-[var(--color-muted)]">
							{formatAgo(r.occurredAt)}
						</td>
						<td className="px-2 py-1.5 font-mono text-[11px] text-[var(--color-muted)]">
							{truncatePayload(r.payload)}
						</td>
					</tr>
				))}
				{rows.length === 0 && (
					<tr>
						<td
							colSpan={4}
							className="px-2 py-2 text-center text-xs text-[var(--color-muted)]"
						>
							No system audit rows yet.
						</td>
					</tr>
				)}
			</Table>
		</PanelShell>
	);
}

function Table({ headers, children }: { headers: string[]; children: React.ReactNode }) {
	return (
		<table className="w-full min-w-full text-left text-sm">
			<thead>
				<tr className="text-xs uppercase tracking-wide text-[var(--color-muted)]">
					{headers.map((h) => (
						<th key={h} className="px-2 py-1.5 font-medium">
							{h}
						</th>
					))}
				</tr>
			</thead>
			<tbody>{children}</tbody>
		</table>
	);
}

// --- formatters -------------------------------------------------------------

function formatDuration(seconds: number): string {
	if (!Number.isFinite(seconds) || seconds < 0) return "—";
	if (seconds < 60) return `${seconds}s`;
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}m`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h`;
	const days = Math.floor(hours / 24);
	return `${days}d`;
}

function formatAgo(iso: string): string {
	const ms = Date.parse(iso);
	if (!Number.isFinite(ms)) return "—";
	const seconds = Math.max(0, Math.floor((Date.now() - ms) / 1000));
	return formatDuration(seconds);
}

function truncatePayload(payload: unknown): string {
	if (payload == null) return "—";
	let s: string;
	try {
		s = typeof payload === "string" ? payload : JSON.stringify(payload);
	} catch {
		return "—";
	}
	if (!s) return "—";
	return s.length > 120 ? `${s.slice(0, 117)}…` : s;
}
